import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Typography,
  InputAdornment,
  IconButton,
  Box,
  Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
// import apiClient from '../config/axios';

const PAGE_SIZE = 10;

const UserSelectionModal = ({ open, onClose, onSelect, users = [] }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const paginatedResults = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSearch = async () => {
    setLoading(true);
    setError('');
    try {
      setSearched(true);
      setPage(1); // resetta la pagina alla ricerca
      if (!search.trim()) {
        setResults(users);
      } else {
        const s = search.toLowerCase();
        const filtered = users.filter(user =>
          user.name?.toLowerCase().includes(s) ||
          user.surname?.toLowerCase().includes(s) ||
          user.email?.toLowerCase().includes(s)
        );
        // console.log('xxxx', users[0]);
        setResults(filtered);
      }
    } catch (e) {
      setError('Errore nella ricerca utenti');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSearch('');
      setResults(users);
      setSearched(false);
      setPage(1);
      setSelectedUser(null); // Reset selected user when modal opens
    }
  }, [open, users]);

  // Quando si digita, nascondi i risultati finché non si cerca
  const showResults = search.trim() === '' || searched;

  const handleSelect = (user) => {
    setSelectedUser(user);
    onSelect(user);
    onClose(); // Chiudi subito il modal dopo la selezione
  };
  const handleDeselect = () => {
    setSelectedUser(null);
  };

  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  return (
    <Dialog 
      open={open} 
      onClose={(e, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose();
      }} 
      fullWidth 
      maxWidth="sm" 
      disableEscapeKeyDown
      sx={{
        '& .MuiDialog-paper': {
          margin: { xs: 1, sm: 2 },
          width: { xs: 'calc(100% - 16px)', sm: 'auto' },
          maxWidth: { xs: '100%', sm: 600 }
        }
      }}
    >
      <DialogTitle sx={{ 
        fontSize: { xs: '1.1rem', sm: '1.25rem' },
        px: { xs: 2, sm: 3 },
        py: { xs: 1.5, sm: 2 }
      }}>
        Seleziona Utente
      </DialogTitle>
      <DialogContent sx={{ 
        px: { xs: 2, sm: 3 },
        pb: { xs: 1, sm: 2 }
      }}>
        {/* Mostra utente selezionato se presente */}
        {selectedUser && (
          <Box sx={{ 
            p: { xs: 1.5, sm: 2 }, 
            mb: 2, 
            borderColor: 'grey.300', 
            borderWidth: 1, 
            borderStyle: 'solid', 
            borderRadius: 2, 
            display: 'flex', 
            alignItems: 'center', 
            width: '100%', 
            minHeight: { xs: 48, sm: 56 },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 }
          }}>
            <Typography 
              variant='body1' 
              fontWeight='bold' 
              noWrap 
              sx={{ 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                flexGrow: 1,
                fontSize: { xs: '0.9rem', sm: '1rem' },
                textAlign: { xs: 'center', sm: 'left' }
              }}
            >
              {selectedUser.name} {selectedUser.surname}
            </Typography>
            <Typography 
              variant='body2' 
              color='text.secondary' 
              sx={{ 
                ml: { xs: 0, sm: 2 },
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                textAlign: { xs: 'center', sm: 'left' }
              }}
            >
              {selectedUser.email}
            </Typography>
            <Tooltip title='Deseleziona utente'>
              <IconButton 
                aria-label='Deseleziona utente' 
                color='error' 
                onClick={handleDeselect} 
                sx={{ 
                  ml: { xs: 0, sm: 2 },
                  mt: { xs: 1, sm: 0 }
                }}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        <TextField
          label="Cerca per nome, cognome o email"
          fullWidth
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => {
            handleSearch();
          }}
          margin="normal"
          sx={{
            '& .MuiInputBase-root': {
              fontSize: { xs: '0.9rem', sm: '1rem' }
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton 
                  onClick={handleSearch} 
                  disabled={loading} 
                  edge="end" 
                  aria-label="cerca"
                  sx={{ 
                    padding: { xs: 1, sm: 1.5 }
                  }}
                >
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        {loading && <CircularProgress size={24} sx={{ ml: 2 }} />}
        {error && <Typography color="error">{error}</Typography>}
        {showResults && (
          <Box sx={{ 
            height: { xs: 250, sm: 300 }, 
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            mt: 1
          }}>
            <Box sx={{ 
              flex: 1, 
              overflow: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: 1
            }}>
              <List dense sx={{ p: 0 }}>
                {paginatedResults.map(user => (
                  <ListItem 
                    key={user._id} 
                    sx={{ 
                      py: { xs: 1.5, sm: 0.5 }, 
                      minHeight: { xs: 80, sm: 36 },
                      px: { xs: 1, sm: 2 },
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'stretch', sm: 'center' },
                      gap: { xs: 1, sm: 0 },
                      width: '100%'
                    }} 
                    secondaryAction={
                      <Button 
                        variant="contained" 
                        size="small" 
                        sx={{ 
                          minWidth: { xs: 'auto', sm: 'auto' }, 
                          px: { xs: 0.5, sm: 1.5 }, 
                          py: { xs: 0.25, sm: 0.5 }, 
                          fontSize: { xs: '0.7rem', sm: '0.85rem' },
                          mt: { xs: 0, sm: 0 },
                          width: { xs: 'auto', sm: 'auto' },
                          height: { xs: 24, sm: 'auto' },
                          minHeight: { xs: 24, sm: 'auto' }
                        }} 
                        onClick={() => handleSelect(user)}
                      >
                        Seleziona
                      </Button>
                    }
                  >
                    <ListItemText
                      primary={
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            fontWeight: 'medium',
                            wordBreak: 'break-word',
                            width: '100%'
                          }}
                        >
                          {user.name} {user.surname}
                        </Typography>
                      }
                      secondary={
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: { xs: '0.8rem', sm: '0.85rem' }, 
                            color: '#888',
                            wordBreak: 'break-word',
                            width: '100%'
                          }}
                        >
                          {user.email}
                        </Typography>
                      }
                      sx={{ 
                        my: 0,
                        width: '100%',
                        '& .MuiListItemText-primary': {
                          fontSize: { xs: '0.9rem', sm: '1rem' }
                        },
                        '& .MuiListItemText-secondary': {
                          fontSize: { xs: '0.8rem', sm: '0.85rem' }
                        }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
            <Box 
              display="flex" 
              justifyContent="center" 
              alignItems="center" 
              mt={1} 
              gap={{ xs: 1, sm: 2 }}
              sx={{
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 2 }
              }}
            >
              <Button 
                onClick={handlePrevPage} 
                disabled={page === 1} 
                size="small"
                sx={{ 
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  px: { xs: 1, sm: 2 }
                }}
              >
                &lt; Precedente
              </Button>
              <Typography 
                variant="body2"
                sx={{ 
                  fontSize: { xs: '0.8rem', sm: '0.875rem' }
                }}
              >
                Pagina {page} di {totalPages}
              </Typography>
              <Button 
                onClick={handleNextPage} 
                disabled={page === totalPages} 
                size="small"
                sx={{ 
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  px: { xs: 1, sm: 2 }
                }}
              >
                Successiva &gt;
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ 
        px: { xs: 2, sm: 3 },
        py: { xs: 1, sm: 2 }
      }}>
        <Button 
          onClick={onClose}
          sx={{ 
            fontSize: { xs: '0.9rem', sm: '1rem' },
            px: { xs: 2, sm: 3 }
          }}
        >
          Chiudi
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserSelectionModal; 